import { useCallback } from 'react';
import { useAuthenticationService } from '../authentication/authentication-service';
import { useVariable } from '../backstage/BackstageProvider';

export const useEmployeeAvatarApi = (employeeId: string) => {
  const [user, getEffectiveTenantId] = useAuthenticationService(state => [state.user, state.getEffectiveTenantId]);
  const apiUrl = useVariable('apiUrl')?.replace('/graphql', '');

  const getRequestHeaders = useCallback(() => {
    const tenantId = getEffectiveTenantId();
    const accessToken = user?.access_token;

    if (!apiUrl || !tenantId || !accessToken) {
      throw new Error('Missing avatar request configuration');
    }

    return {
      apiUrl,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'tenant-id': tenantId
      }
    };
  }, [apiUrl, getEffectiveTenantId, user?.access_token]);

  const uploadAvatar = useCallback(
    async (file: File) => {
      const { apiUrl: baseUrl, headers } = getRequestHeaders();
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${baseUrl}/api/employees/${employeeId}/avatar`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to upload avatar');
      }
    },
    [employeeId, getRequestHeaders]
  );

  const removeAvatar = useCallback(async () => {
    const { apiUrl: baseUrl, headers } = getRequestHeaders();

    const response = await fetch(`${baseUrl}/api/employees/${employeeId}/avatar`, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to remove avatar');
    }
  }, [employeeId, getRequestHeaders]);

  return {
    uploadAvatar,
    removeAvatar
  };
};
